import React from "react";
import { apiClient } from "../../lib/api";

// Kaizen Email Trigger admin panel — probe/status controls for the hybrid
// push+poll email trigger loop. Off until env vars set; UI reflects state.

export default function KaizenEmailTriggerPanel() {
  const [status, setStatus] = React.useState(null);
  const [probe, setProbe] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [probing, setProbing] = React.useState(false);
  const [error, setError] = React.useState("");
  const [includeSeen, setIncludeSeen] = React.useState(false);
  const [replaying, setReplaying] = React.useState(false);
  const [replayResult, setReplayResult] = React.useState(null);

  const runReplayLast = async () => {
    setReplaying(true);
    setReplayResult(null);
    try {
      const r = await apiClient.post(`/kaizen/email-trigger/replay-last`);
      setReplayResult(r.data);
    } catch (e) {
      setReplayResult({ ok: false, error: e?.response?.data?.detail || e?.message || "Replay failed" });
    } finally {
      setReplaying(false);
    }
  };

  const loadStatus = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await apiClient.get(`/kaizen/email-trigger/status`);
      setStatus(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load status");
    } finally {
      setLoading(false);
    }
  }, []);

  const runProbe = async () => {
    setProbing(true);
    setError("");
    try {
      const r = await apiClient.post(`/kaizen/email-trigger/probe${includeSeen ? "?include_seen=true" : ""}`);
      setProbe(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Probe failed");
    } finally {
      setProbing(false);
    }
  };

  React.useEffect(() => { loadStatus(); }, [loadStatus]);

  const configured = status?.configured === true;

  return (
    <section
      className="rounded-2xl border border-[#E2E0D8] bg-white p-5 astat-fade-in"
      data-testid="kaizen-email-panel"
    >
      <header className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs font-semibold tracking-[0.2em] text-forest/70 uppercase">
            GW-6 · Kaizen Email Trigger
          </div>
          <h2 className="text-xl font-bold text-slate_navy" data-testid="kaizen-email-title">
            Email → Kaizen scan trigger
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Watches <code className="text-xs bg-bone px-1 py-0.5 rounded">info@medexova.com</code>{" "}
            every {status?.interval_seconds || 15}s. When Kaizen emails a new trip offer, extracts the ride number and full offer details (passenger, phone, pickup, dropoff, vehicle type) straight from the email body and <b>calls/texts the on-duty VA instantly</b> — that notification no longer waits on the portal bot at all. The portal bot then runs in parallel, best-effort, to confirm the offer is still live and click Accept.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={runReplayLast}
            disabled={replaying}
            data-testid="kaizen-email-replay-last-btn"
            className="text-sm px-3 py-1.5 border border-forest/40 text-forest rounded-md hover:bg-forest/5 font-semibold disabled:opacity-50"
            title="Re-parses your actual last Kaizen email and places a REAL test call/text using the live script — never touches the Kaizen portal."
          >
            {replaying ? "Calling…" : "☏ Replay last email"}
          </button>
          <button
            onClick={loadStatus}
            data-testid="kaizen-email-refresh"
            className="text-sm px-3 py-1.5 border border-[#E2E0D8] rounded-md hover:bg-forest/5"
          >
            ↻ Refresh
          </button>
        </div>
      </header>

      {replayResult && (
        <div
          className={`mb-3 rounded-lg border px-3 py-2 text-sm ${replayResult.ok ? "border-forest/40 bg-forest/5 text-forest" : "border-rust/50 bg-rust/5 text-rust"}`}
          data-testid="kaizen-email-replay-result"
        >
          {replayResult.ok ? (
            <>
              <div className="font-semibold">Test call placed off your real last email — "{replayResult.subject || "(no subject)"}"</div>
              <div className="text-xs mt-1 text-slate-600">
                Real ride on file: <b className="font-mono">{replayResult.real_ride_id || "—"}</b> · voice result: <b>{replayResult.voice_auth_result?.decision || "—"}</b> · Kaizen portal was NOT touched.
              </div>
            </>
          ) : (
            <div className="font-semibold">{replayResult.error || "Replay failed"}</div>
          )}
        </div>
      )}

      {error && (
        <div className="mb-3 rounded-lg border border-rust/50 bg-rust/5 px-3 py-2 text-sm text-rust" data-testid="kaizen-email-error">
          {error}
        </div>
      )}

      {/* Status card */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatusCard
          label="State"
          value={loading ? "…" : (configured ? "ACTIVE" : "DISABLED")}
          tone={configured ? "green" : "amber"}
          testid="kaizen-email-status-state"
        />
        <StatusCard
          label="Mailbox"
          value={status?.mailbox || "—"}
          testid="kaizen-email-status-mailbox"
        />
        <StatusCard
          label="Interval"
          value={status?.interval_seconds ? `${status.interval_seconds}s` : "—"}
          testid="kaizen-email-status-interval"
        />
        <StatusCard
          label="Sender match"
          value={status?.from_match || "—"}
          mono
          testid="kaizen-email-status-match"
        />
      </div>

      {!configured && (
        <div className="mb-5 rounded-lg border border-amber-400/40 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="kaizen-email-config-hint">
          <div className="font-semibold mb-1">Not yet configured.</div>
          <div className="text-xs">
            Paste into <code>backend/.env</code>, then restart backend:
          </div>
          <pre className="mt-2 bg-white border border-amber-300 rounded p-2 text-[11px] font-mono overflow-x-auto">
{`KAIZEN_EMAIL_IMAP_HOST=imap.gmail.com
KAIZEN_EMAIL_IMAP_USER=info@medexova.com
KAIZEN_EMAIL_IMAP_PASS=<app-specific password>`}
          </pre>
        </div>
      )}

      {/* Probe */}
      <div className="rounded-lg border border-[#E2E0D8] bg-bone/40 p-4">
        <div className="flex items-start justify-between mb-3 gap-4">
          <div className="flex-1">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">
              Diagnostic probe
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              Pull one live batch from the inbox and see everything the scanner sees — including senders we <em>skipped</em>.
              Enable <b>include already-read mail</b> to retro-scan mails Gmail has marked as seen.
            </div>
            <label className="flex items-center gap-2 mt-2 text-xs text-slate-700 cursor-pointer" data-testid="kaizen-email-include-seen">
              <input
                type="checkbox"
                checked={includeSeen}
                onChange={(e) => setIncludeSeen(e.target.checked)}
                className="rounded border-[#E2E0D8]"
              />
              Include already-read mail (retro-scan without marking seen)
            </label>
          </div>
          <button
            onClick={runProbe}
            disabled={!configured || probing}
            data-testid="kaizen-email-probe-btn"
            className="text-sm font-bold px-4 py-2 rounded-md bg-forest text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {probing ? "Probing…" : "Run probe now"}
          </button>
        </div>

        {probe && (
          <div className="mt-3" data-testid="kaizen-email-probe-result">
            {probe.ok === false ? (
              <div className="text-sm text-rust font-semibold">
                {probe.error || "Probe returned an error"}
              </div>
            ) : (
              <>
                {/* Diagnostic breakdown — surfaces WHY messages were / weren't matched. */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3 text-[11px]" data-testid="kaizen-email-probe-summary">
                  <div className="bg-white border border-[#E2E0D8] rounded p-2">
                    <div className="uppercase tracking-wider text-slate-500 font-semibold">Mailbox</div>
                    <div className="font-bold text-slate_navy mt-0.5">{probe.mailbox} · {probe.mailbox_total || 0} total</div>
                  </div>
                  <div className="bg-white border border-[#E2E0D8] rounded p-2">
                    <div className="uppercase tracking-wider text-slate-500 font-semibold">
                      {probe.include_seen ? "All (rescan)" : "Unseen"}
                    </div>
                    <div className="font-bold text-slate_navy mt-0.5">{probe.unseen_total || 0}</div>
                  </div>
                  <div className="bg-white border border-forest/25 rounded p-2 bg-forest/5">
                    <div className="uppercase tracking-wider text-forest/80 font-semibold">Matched</div>
                    <div className="font-bold text-forest mt-0.5">{probe.matched_count || 0}</div>
                  </div>
                  <div className="bg-white border border-[#E2E0D8] rounded p-2">
                    <div className="uppercase tracking-wider text-slate-500 font-semibold">Sender regex</div>
                    <div className="font-mono text-slate_navy mt-0.5 truncate">/{probe.sender_regex || "kaizen"}/i</div>
                  </div>
                </div>

                {(probe.unmatched_examples || []).length > 0 && (
                  <details className="mb-3 bg-white border border-[#E2E0D8] rounded p-2" data-testid="kaizen-email-unmatched">
                    <summary className="text-xs font-semibold text-slate-700 cursor-pointer">
                      · {probe.unmatched_examples.length} skipped mails (didn&apos;t match sender regex)
                    </summary>
                    <ul className="mt-2 space-y-1 text-[11px]">
                      {probe.unmatched_examples.map((u, i) => (
                        <li key={i} className="border-b border-slate-100 pb-1">
                          <div className="text-slate-700 truncate"><b>from:</b> {u.from}</div>
                          <div className="text-slate-500 truncate"><b>subj:</b> {u.subject}</div>
                        </li>
                      ))}
                    </ul>
                    <div className="text-[11px] text-slate-500 mt-2 italic">
                      To broaden matching, edit <code className="font-mono bg-bone px-1 rounded">KAIZEN_EMAIL_FROM_MATCH</code> in <code>backend/.env</code>.
                    </div>
                  </details>
                )}

                <div className="text-sm mb-2">
                  Matched <b>{probe.matched_count || 0}</b>{" "}
                  {probe.matched_count === 1 ? "email" : "emails"}
                </div>
                {(probe.detail || []).length === 0 ? (
                  <div className="text-xs text-slate-500 italic">No Kaizen mail in this batch.</div>
                ) : (
                  <ul className="space-y-2 astat-fade-stagger" key={JSON.stringify(probe.detail)}>
                    {(probe.detail || []).map((e, i) => {
                      const po = e.parsed_offer || {};
                      return (
                        <li key={i} className="border border-[#E2E0D8] bg-white rounded p-3" data-testid={`kaizen-email-envelope-${i}`}>
                          <div className="text-sm font-semibold text-slate_navy truncate">{e.subject || "(no subject)"}</div>
                          <div className="text-[11px] text-slate-500 mb-1">from: {e.from}</div>
                          <div className="text-xs">
                            Ride #: <b className="font-mono">{(e.trip_ids || []).join(", ") || "—"}</b>
                            {e.portal_offer_id && (
                              <>  ·  Portal offer: <b className="font-mono">{e.portal_offer_id}</b></>
                            )}
                          </div>
                          {(po.passenger_name || po.pickup_address) && (
                            <div className="mt-2 grid grid-cols-2 gap-2 text-[11px] text-slate-700">
                              {po.passenger_name && <div><b>Passenger:</b> {po.passenger_name}</div>}
                              {po.passenger_phone && <div><b>Phone:</b> {po.passenger_phone}</div>}
                              {po.pickup_address && <div className="col-span-2"><b>Pickup:</b> {po.pickup_address}</div>}
                              {po.dropoff_address && <div className="col-span-2"><b>Drop-off:</b> {po.dropoff_address}</div>}
                              {po.pickup_date && <div><b>Date:</b> {po.pickup_date}</div>}
                              {po.pickup_time && <div><b>Time:</b> {po.pickup_time}</div>}
                              {po.mobility_type && <div><b>Vehicle:</b> {po.mobility_type}{po.round_trip ? " · roundtrip" : ""}</div>}
                              {po.originating_partner && <div><b>Partner:</b> {po.originating_partner}</div>}
                            </div>
                          )}
                          {e.already_seen?.length > 0 && (
                            <div className="text-[11px] text-slate-500 mt-1">
                              Already in trips: <span className="font-mono">{e.already_seen.join(", ")}</span>
                            </div>
                          )}
                          <div className={"text-[11px] mt-1 font-semibold " + (e.would_trigger ? "text-forest" : "text-slate-500")}>
                            {e.would_trigger ? "→ WOULD fire an instant call/SMS + wake the portal bot" : "· dedup — no call needed"}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function StatusCard({ label, value, tone = "slate", testid, mono = false }) {
  const toneClasses = {
    slate: "border-[#E2E0D8] bg-bone/40 text-slate_navy",
    green: "border-forest/25 bg-forest/5 text-forest",
    amber: "border-amber-400/40 bg-amber-50 text-amber-700",
  }[tone] || "border-[#E2E0D8] bg-bone/40 text-slate_navy";
  return (
    <div className={"rounded-xl border p-4 " + toneClasses} data-testid={testid}>
      <div className="text-[11px] font-semibold uppercase tracking-wider opacity-75">{label}</div>
      <div className={"mt-1 font-bold text-xl " + (mono ? "font-mono text-sm" : "")}>{value}</div>
    </div>
  );
}
